"use client";

import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Table from "../components/common/Table";
import Modal from "../components/common/Modal";
import api from "../services/api";
import certificateService from "../services/certificateService";
import { CERTIFICATIONS } from "../utils/constants";
import Swal from "sweetalert2";
import html2pdf from "html2pdf.js";

const Certifications = () => {
  const [certificates, setCertificates] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [formData, setFormData] = useState({
    studentId: "",
    courseId: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsRes, coursesRes, certificatesRes] = await Promise.all([
        api.get("/users?role=student"),
        api.get("/courses"),
        api.get("/certifications"),
      ]);

      // Get students data and filter in case the backend returns extra users
      const studentData = studentsRes.data?.data || studentsRes.data || [];
      const filteredStudents = studentData.filter((user) => {
        if (typeof user.role === "object" && user.role !== null) {
          return user.role.name === "student";
        }
        return user.role === "student";
      });

      setStudents(filteredStudents);
      setCourses(coursesRes.data?.data || coursesRes.data || []);
      setCertificates(certificatesRes.data?.data || certificatesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(error.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleStudentChange = (e) => {
    const studentId = e.target.value;
    const student = students.find((s) => s._id === studentId) || null;
    setSelectedStudent(student);
    setFormData({
      ...formData,
      studentId,
    });
  };

  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    const course = courses.find((c) => c._id === courseId) || null;
    setSelectedCourse(course);
    setFormData({
      ...formData,
      courseId,
    });
  };

  const handleGenerateCertificate = async () => {
    try {
      const { studentId, courseId } = formData;

      if (!studentId || !courseId) {
        toast.error("Please select both student and course");
        return;
      }

      const response = await certificateService.generateCertificate(
        studentId,
        courseId,
        {
          expirationDate: formData.expirationDate,
        }
      );

      toast.success("Certificate generated successfully");
      setShowGenerateModal(false);
      fetchData();
    } catch (error) {
      console.error("Error generating certificate:", error);
      toast.error(
        error.response?.data?.message || "Failed to generate certificate"
      );
    }
  };

  const handleRevokeCertificate = async (certificateId) => {
    if (window.confirm("Are you sure you want to revoke this certificate?")) {
      try {
        await certificateService.updateCertificate(certificateId, {
          status: "Revoked",
        });

        toast.success("Certificate revoked successfully");
        fetchData();
      } catch (error) {
        console.error("Error revoking certificate:", error);
        toast.error(
          error.response?.data?.message || "Failed to revoke certificate"
        );
      }
    }
  };

  const handleDownloadCertificate = async (serialNumber) => {
    try {
      Swal.fire({
        title: "Downloading...",
        text: "Please wait while we prepare your certificate.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await certificateService.downloadCertificate(
        serialNumber
      );

      // Check if response is ok
      if (!response || response.status !== 200) {
        throw new Error("Failed to fetch certificate.");
      }

      // Convert response to blob
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `certificate-${serialNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.close();
    } catch (error) {
      console.error("Download failed:", error);

      let errorMessage = "Failed to download certificate.";
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = "Certificate not found.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        }
      }

      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: errorMessage,
      });
    }
  };

  const handleVerifyCertificate = async (serialNumber) => {
    try {
      const response = await certificateService.verifyCertificate(serialNumber);
      toast.success("Certificate is valid");
      console.log("Certificate details:", response);
    } catch (error) {
      console.error("Error verifying certificate:", error);
      toast.error("Certificate is invalid or not found");
    }
  };

  async function handleOnClick() {
    html2pdf(document.querySelector(".certificate-preview"), {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `${selectedCertificate.serialNumber}.pdf`,
      html2canvas: {
        scale: 2,
        letterRendering: true,
      },
      jsPDF: {
        unit: "in",
        format: [8.5, 11],
        orientation: "landscape",
      },
    });
  }

  const columns = [
    {
      field: "student",
      header: "Student",
      render: (cert) => {
        if (cert.student) {
          return `${cert.student.firstName} ${cert.student.lastName}`;
        }
        return "Unknown Student";
      },
    },
    {
      field: "course",
      header: "Course",
      render: (cert) => cert.course?.name || "Unknown Course",
    },
    {
      field: "serialNumber",
      header: "Certificate Number",
    },
    {
      field: "issueDate",
      header: "Issue Date",
      render: (cert) => new Date(cert.issueDate).toLocaleDateString(),
    },
    {
      field: "actions",
      header: "Actions",
      render: (cert) => (
        <div className="flex gap-2 action-buttons">
          <button
            className="px-4 py-2 text-white transition-all bg-blue-600 rounded-md view-button hover:bg-blue-700"
            onClick={() => {
              setSelectedCertificate(cert);
              setShowPreviewModal(true);
            }}
          >
            Preview
          </button>
          <button
            className="px-4 py-2 text-white transition-all bg-yellow-500 rounded-md verify-button hover:bg-yellow-600"
            onClick={() => handleVerifyCertificate(cert.serialNumber)}
          >
            Verify
          </button>
          {cert.status === "Issued" && (
            <button
              className="px-4 py-2 text-white transition-all bg-red-600 rounded-md delete-button hover:bg-red-700"
              onClick={() => handleRevokeCertificate(cert._id)}
            >
              Revoke
            </button>
          )}
        </div>
      ),
    },
  ];

  const CertificatePreview = ({ certificate }) => {
    if (!certificate) return null;

    return (
      <div className="flex items-center justify-center p-4">
        <div className="relative w-full max-w-4xl certificate-preview">
          <img
            src="/Certificate-Template.png"
            alt="Certificate Template"
            className="w-full h-auto"
          />

          <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
            <p className="absolute top-[44%] text-4xl font-bold text-black font-montserrat uppercase">
              {certificate.student?.firstName?.toUpperCase()}{" "}
              {certificate.student?.lastName?.toUpperCase()}
            </p>

            <p className="absolute top-[55%] left-[56%] text-sm font-semibold text-gray-800 font-serif">
              {certificate.course?.name}
            </p>

            <p className="absolute bottom-[37%] left-[19%] text-sm">
              {new Date(certificate.issueDate).toLocaleDateString("en-US")}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 space-y-8 bg-white rounded-lg shadow-xl certifications-container">
      <div className="flex items-center justify-between mb-8 page-header">
        <h1 className="text-3xl font-semibold text-gray-900">Certifications</h1>
        <button
          className="px-8 py-3 text-white transition-all bg-indigo-600 rounded-lg shadow-md add-button hover:bg-indigo-700"
          onClick={() => setShowGenerateModal(true)}
        >
          Generate Certificate
        </button>
      </div>

      {loading ? (
        <div className="py-6 text-xl text-center text-gray-500 loading-spinner">
          <div className="w-12 h-12 mx-auto border-b-4 border-indigo-600 rounded-full animate-spin"></div>
          <p className="mt-4 text-lg text-gray-600">Loading certificates...</p>
        </div>
      ) : (
        <Table data={certificates} columns={columns} />
      )}

      {showGenerateModal && (
        <Modal
          title="Generate Certificate"
          onClose={() => setShowGenerateModal(false)}
          size="large"
        >
          <div className="space-y-6">
            <div className="form-group">
              <label
                htmlFor="studentId"
                className="block text-sm font-medium text-gray-700"
              >
                Student
              </label>
              <select
                id="studentId"
                name="studentId"
                value={formData.studentId}
                onChange={handleStudentChange}
                className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select Student</option>
                {students.map((student) => (
                  <option key={student._id} value={student._id}>
                    {student.firstName} {student.lastName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label
                htmlFor="course"
                className="block mb-2 font-medium text-gray-700"
              >
                Select Course
              </label>
              <select
                id="course"
                className="block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm form-control focus:ring-indigo-500 focus:border-indigo-500"
                onChange={handleCourseChange}
                value={selectedCourse?._id || ""}
              >
                <option value="">-- Select a course --</option>
                {courses.length > 0 ? (
                  courses.map((course) => (
                    <option key={course._id} value={course._id}>
                      {course.name}
                    </option>
                  ))
                ) : (
                  <option disabled>No courses available</option>
                )}
              </select>
            </div>

            <div className="form-group">
              <label
                htmlFor="expirationDate"
                className="block text-sm font-medium text-gray-700"
              >
                Expiration Date
              </label>
              <input
                type="date"
                id="expirationDate"
                name="expirationDate"
                value={formData.expirationDate}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="flex justify-end space-x-4 form-actions">
              <button
                type="button"
                className="px-6 py-2 text-white transition-all bg-gray-500 rounded-md cancel-button hover:bg-gray-600"
                onClick={() => setShowGenerateModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2 text-white transition-all bg-indigo-600 rounded-md generate-button hover:bg-indigo-700"
                onClick={handleGenerateCertificate}
              >
                Generate Certificate
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showPreviewModal && selectedCertificate && (
        <Modal
          title="Certificate Preview"
          onClose={() => setShowPreviewModal(false)}
          size="large"
        >
          <CertificatePreview certificate={selectedCertificate} />
          <div className="flex justify-end mt-6 space-x-4 modal-actions">
            <button
              className="px-6 py-2 text-white transition-all bg-blue-600 rounded-md download-button hover:bg-blue-700"
              onClick={handleOnClick}
            >
              Download PDF
            </button>
            <button
              className="px-6 py-2 text-white transition-all bg-green-600 rounded-md email-button hover:bg-green-700"
              onClick={() =>
                toast.info("Email functionality will be implemented soon")
              }
            >
              Email to Student
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Certifications;
